import { useEffect, useRef, useState } from "react";
import { PaperPlaneRight, Sparkle, Robot, CalendarCheck, Clock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";

function Receptionist() {
  const { t, lang } = useI18n();
  const SUGGESTIONS = [t.asst.sugg1, t.asst.sugg2, t.asst.sugg3, t.asst.sugg4];
  const loc = lang === "es" ? "es-ES" : "en-US";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/receptionist", { message: msg, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI error");
      setMessages((m) => [...m, { role: "assistant", text: t.asst.error }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E5E2DC] flex flex-col h-[calc(100vh-280px)] min-h-[420px]" data-testid="receptionist-panel">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="h-14 w-14 rounded-2xl bg-[#2C3E35] flex items-center justify-center mb-4">
              <Robot size={28} weight="duotone" color="#FDFBF9" />
            </div>
            <h3 className="font-heading text-xl font-medium">{t.asst.recepTitle}</h3>
            <p className="text-sm text-[#6B6A67] mt-1 max-w-sm">{t.asst.recepSub}</p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-md">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="ai-suggestion" onClick={() => send(s)}
                  className="text-xs px-3 py-2 rounded-full border border-[#E5E2DC] text-[#3a3a38] hover:bg-[#E8E4DF] transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-[#2C3E35] text-white rounded-br-md" : "bg-[#F2EFEA] text-[#1A1A1A] rounded-bl-md"
            }`}>{m.text}</div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#F2EFEA] px-4 py-3 rounded-2xl rounded-bl-md flex gap-1">
              <span className="h-2 w-2 rounded-full bg-[#6B6A67] animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="h-2 w-2 rounded-full bg-[#6B6A67] animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="h-2 w-2 rounded-full bg-[#6B6A67] animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="p-4 border-t border-[#E5E2DC]">
        <div className="flex gap-2">
          <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t.asst.placeholder}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition text-sm" />
          <button data-testid="ai-send" onClick={() => send()} disabled={loading}
            className="px-4 rounded-lg bg-[#2C3E35] text-white hover:bg-[#1E2B25] transition-colors disabled:opacity-60">
            <PaperPlaneRight size={18} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Scheduler() {
  const { t, lang } = useI18n();
  const loc = lang === "es" ? "es-ES" : "en-US";
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/services").then(({ data }) => { setServices(data); if (data[0]) setServiceId(data[0].id); }); }, []);

  const find = async () => {
    if (!serviceId) { toast.error(t.asst.fService); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/scheduler", { service_id: serviceId, date });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E5E2DC] p-6" data-testid="scheduler-panel">
      <div className="flex items-center gap-2 mb-5">
        <CalendarCheck size={20} weight="duotone" color="#2C3E35" />
        <h3 className="font-heading text-lg font-medium">{t.asst.schedTitle}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label-caps block mb-1.5">{t.asst.fService}</label>
          <select data-testid="scheduler-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] transition text-sm">
            {services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_min}min</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">{t.asst.fDate}</label>
          <input data-testid="scheduler-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] transition text-sm" />
        </div>
        <div className="flex items-end">
          <button data-testid="scheduler-find" onClick={find} disabled={loading}
            className="w-full px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors disabled:opacity-60">
            {loading ? t.asst.finding : t.asst.find}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-[#6B6A67]">{t.asst.openSlots} <span className="font-semibold text-[#1A1A1A]">{result.service}</span> ({result.duration_min} min) · {new Date(result.date + "T00:00:00").toLocaleDateString(loc, { weekday: "long", month: "long", day: "numeric" })}</p>
          {result.suggestions.length === 0 ? (
            <p className="text-sm text-[#B86B5A]">{t.asst.noAvail}</p>
          ) : result.suggestions.map((s) => (
            <div key={s.staff_id} className="border border-[#E5E2DC] rounded-lg p-4">
              <div className="font-semibold text-sm mb-3">{s.staff_name}</div>
              <div className="flex flex-wrap gap-2">
                {s.slots.map((slot) => (
                  <span key={slot} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#7A8B5C]/12 text-[#5d6b46]">
                    <Clock size={13} /> {slot}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AIAssistant() {
  const { t } = useI18n();
  const [tab, setTab] = useState("receptionist");
  return (
    <div className="fade-up space-y-6" data-testid="assistant-page">
      <div>
        <p className="label-caps">{t.asst.overline}</p>
        <h1 className="font-heading text-3xl font-light tracking-tight mt-1 flex items-center gap-2">
          {t.asst.title} <Sparkle size={24} weight="fill" color="#B86B5A" />
        </h1>
      </div>
      <div className="flex gap-2">
        {[{ k: "receptionist", l: t.asst.tabRecep }, { k: "scheduler", l: t.asst.tabSched }].map((tb) => (
          <button key={tb.k} data-testid={`ai-tab-${tb.k}`} onClick={() => setTab(tb.k)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === tb.k ? "bg-[#2C3E35] text-white" : "bg-white border border-[#E5E2DC] text-[#6B6A67] hover:bg-[#E8E4DF]"}`}>{tb.l}</button>
        ))}
      </div>
      {tab === "receptionist" ? <Receptionist /> : <Scheduler />}
    </div>
  );
}
